import React, { useEffect, useState, useMemo } from "react";
import { useParams } from 'react-router-dom';
import { useNavigateWithRange } from '../hooks/useNavigateWithRange';
import { useModelList } from '../services/modelsApi';
import { LOADING_MESSAGE } from '../utils/strings';
import ResizableTable from '../components/ResizableTable';
import { components, iconSize, iconShape, layout } from '../utils/designTokens';
import { formatDuration, formatCost, formatTokens } from '../utils/formatters';

const FilterChip = ({ label, onRemove }) => (
  <span className="inline-flex items-center gap-1 px-2 py-1 bg-accent-amber/20 border border-accent-amber/50 rounded text-xs text-text-primary">
    {label}
    <button onClick={onRemove} className="hover:text-danger ml-1">×</button>
  </span>
);

const formatTimeAgo = (timestamp) => {
  if (!timestamp) return '—';
  const now = Date.now();
  const then = new Date(timestamp).getTime();
  const diff = now - then;
  const minutes = Math.floor(Math.abs(diff) / 60000);

  if (minutes < 1) return 'Just now';
  if (minutes < 60) return `${minutes}m ago`;
  const hours = Math.floor(minutes / 60);
  if (hours < 24) return `${hours}h ago`;
  const days = Math.floor(hours / 24);
  return `${days}d ago`;
};

const VirtualModelRow = React.memo(({ data, index, style }) => {
  const { data: models, columnWidths, onOpen } = data;
  const model = models[index];

  const isRed = model.error_rate > 10 || (model.security_alerts || 0) > 0;
  const dotColor = isRed ? 'bg-danger' : 'bg-pastel-teal';
  const last = formatTimeAgo(model.last_seen);
  const agentsDisplay = model.agents && model.agents.length > 0 ? model.agents.join(', ') : '—';

  return (
    <div
      style={style}
      onClick={() => onOpen(model.name)}
      className={`${components.table.row.container} ${components.table.row.border} ${components.table.row.hover} cursor-pointer`}
    >
      {/* Entity: Model Name */}
      <div
        className={`flex items-center ${components.table.cell.gap} ${components.table.cell.padding}`}
        style={{ width: `${columnWidths.model}px`, minWidth: `${columnWidths.model}px`, flex: 'none' }}
      >
        <div className={`${iconSize.dot} ${iconShape.dot} flex-shrink-0 ${dotColor}`}></div>
        <span className={`${components.table.cell.text.id} truncate`} style={{ color: components.table.cell.colors.entity }}>{model.name}</span>
      </div>
      <div style={{ width: '3px', flex: 'none' }} />
      {/* Metric: Provider */}
      <div
        className={`${components.table.cell.padding} ${components.table.cell.text.metric} capitalize`}
        style={{ width: `${columnWidths.provider}px`, minWidth: `${columnWidths.provider}px`, flex: 'none', color: components.table.cell.colors.metric }}
      >
        {model.provider || '—'}
      </div>
      <div style={{ width: '3px', flex: 'none' }} />
      {/* Metric: Calls */}
      <div
        className={`${components.table.cell.padding} ${components.table.cell.text.metric}`}
        style={{ width: `${columnWidths.calls}px`, minWidth: `${columnWidths.calls}px`, flex: 'none', color: components.table.cell.colors.metric }}
        title="LLM calls in window"
      >
        {model.calls || 0}
      </div>
      <div style={{ width: '3px', flex: 'none' }} />
      {/* Metric: Duration */}
      <div
        className={`${components.table.cell.padding} ${components.table.cell.text.metric}`}
        style={{ width: `${columnWidths.duration}px`, minWidth: `${columnWidths.duration}px`, flex: 'none', color: components.table.cell.colors.metric }}
      >
        {formatDuration(model.total_duration)}
      </div>
      <div style={{ width: '3px', flex: 'none' }} />
      {/* Metric: Error Rate */}
      <div
        className={`${components.table.cell.padding} ${components.table.cell.text.metric}`}
        style={{ width: `${columnWidths.errorRate}px`, minWidth: `${columnWidths.errorRate}px`, flex: 'none', color: components.table.cell.colors.metric }}
      >
        {(model.error_rate || 0).toFixed(1)}%
      </div>
      <div style={{ width: '3px', flex: 'none' }} />
      {/* Metric: Prompt Tokens */}
      <div
        className={`${components.table.cell.padding} ${components.table.cell.text.metric}`}
        style={{ width: `${columnWidths.promptTokens}px`, minWidth: `${columnWidths.promptTokens}px`, flex: 'none', color: components.table.cell.colors.metric }}
      >
        {formatTokens(model.prompt_tokens || 0)}
      </div>
      <div style={{ width: '3px', flex: 'none' }} />
      {/* Metric: Completion Tokens */}
      <div
        className={`${components.table.cell.padding} ${components.table.cell.text.metric}`}
        style={{ width: `${columnWidths.completionTokens}px`, minWidth: `${columnWidths.completionTokens}px`, flex: 'none', color: components.table.cell.colors.metric }}
      >
        {formatTokens(model.completion_tokens || 0)}
      </div>
      <div style={{ width: '3px', flex: 'none' }} />
      {/* Metric: Total Tokens */}
      <div
        className={`${components.table.cell.padding} ${components.table.cell.text.metric}`}
        style={{ width: `${columnWidths.totalTokens}px`, minWidth: `${columnWidths.totalTokens}px`, flex: 'none', color: components.table.cell.colors.metric }}
      >
        {formatTokens(model.total_tokens || 0)}
      </div>
      <div style={{ width: '3px', flex: 'none' }} />
      {/* Metric: Cost */}
      <div
        className={`${components.table.cell.padding} ${components.table.cell.text.metric}`}
        style={{ width: `${columnWidths.cost}px`, minWidth: `${columnWidths.cost}px`, flex: 'none', color: components.table.cell.colors.metric }}
      >
        {formatCost(model.cost)}
      </div>
      <div style={{ width: '3px', flex: 'none' }} />
      {/* Metric: Agents */}
      <div
        className={`${components.table.cell.padding} ${components.table.cell.text.metric} truncate`}
        style={{ width: `${columnWidths.agents}px`, minWidth: `${columnWidths.agents}px`, flex: 'none', color: components.table.cell.colors.metric }}
        title={agentsDisplay}
      >
        {agentsDisplay}
      </div>
      <div style={{ width: '3px', flex: 'none' }} />
      {/* Time: Last Activity */}
      <div
        className={`${components.table.cell.padding} ${components.table.cell.text.muted}`}
        style={{ width: `${columnWidths.lastActivity}px`, minWidth: `${columnWidths.lastActivity}px`, flex: 'none', color: components.table.cell.colors.time }}
      >
        {last}
      </div>
    </div>
  );
});

export default function Models({ onTabChange, onKpisReady }) {
  const { selectedAppId } = useParams();
  const navigate = useNavigateWithRange();

  const [filters, setFilters] = useState({});
  const [query, setQuery] = useState('');

  const { models: modelsData, loading, error } = useModelList(selectedAppId);

  // Calculate global KPIs from models data
  const globalKPIs = useMemo(() => {
    if (!modelsData.length) return null;

    const totalModels = modelsData.length;
    const totalCalls = modelsData.reduce((sum, model) => sum + (model.calls || 0), 0);
    const totalErrors = modelsData.reduce((sum, model) => {
      const errorCount = Math.round((model.error_rate || 0) / 100 * (model.calls || 0));
      return sum + errorCount;
    }, 0);
    const errorRate = totalCalls > 0 ? (totalErrors / totalCalls) * 100 : 0;

    const tokens = modelsData.reduce((sum, model) => sum + (model.total_tokens || 0), 0);
    const totalCost = modelsData.reduce((sum, model) => sum + (model.cost || 0), 0);
    const agentsDistinct = new Set(modelsData.flatMap(m => m.agents || [])).size;
    const securityAlerts = modelsData.reduce((sum, model) => sum + (model.security_alerts || 0), 0);

    return {
      totalModels,
      errorRate,
      tokens,
      totalCost,
      agentsDistinct,
      securityAlerts
    };
  }, [modelsData]);

  const applyFilters = (list) => {
    let out = [...list];
    if (filters.hasErrors) out = out.filter(m => (m.error_rate || 0) > 0);
    if (filters.tokensGtPctl) {
      const tokens = [...list].map(m => m.total_tokens || 0).sort((a, b) => a - b);
      const idx = Math.floor(tokens.length * (filters.tokensGtPctl / 100));
      const thr = tokens[idx] || 0;
      out = out.filter(m => (m.total_tokens || 0) >= thr);
    }
    if (filters.costGtPctl) {
      const costs = [...list].map(m => m.cost || 0).sort((a, b) => a - b);
      const idx = Math.floor(costs.length * (filters.costGtPctl / 100));
      const thr = costs[idx] || 0;
      out = out.filter(m => (m.cost || 0) >= thr);
    }
    if (filters.agentsGt) out = out.filter(m => (m.agents || []).length > filters.agentsGt);
    if (query.trim()) {
      const q = query.toLowerCase();
      out = out.filter(m => m.name.toLowerCase().includes(q));
    }
    return out;
  };

  const filteredModels = applyFilters(modelsData);
  // Models lives only under Journeys, so tag the Traces breadcrumb back to it.
  const openModel = (modelName) => navigate(`/app/${selectedAppId}/traces`, { model: modelName, from: 'journeys-models' });

  // Pass KPIs to parent component
  useEffect(() => {
    if (onKpisReady && globalKPIs) {
      onKpisReady({
        kpis: globalKPIs,
        filters: filters,
        setFilters: setFilters
      });
    }
  }, [globalKPIs, filters]); // Removed onKpisReady - setState functions are stable

  if (loading) {
    return <div className={components.table.empty}>{LOADING_MESSAGE}</div>;
  }

  if (error) {
    return (
      <div className="p-6">
        <div className="text-center py-8">
          <h3 className="text-lg font-medium text-text-primary mb-2">Error Loading Models</h3>
          <p className="text-danger">{error}</p>
        </div>
      </div>
    );
  }

  return (
    <div className="h-full flex flex-col">
      {filteredModels.length === 0 ? (
        <div className={components.table.empty}>
          {modelsData.length === 0
            ? "No models found. Models will appear here when your service generates LLM telemetry data."
            : "No models match."}
        </div>
      ) : (
        <div className="flex-1 min-h-0">
          <ResizableTable
            columns={[
              { key: 'model', label: 'Model', defaultWidth: 200, minWidth: 120 },
              { key: 'provider', label: 'Provider', defaultWidth: 110, minWidth: 80 },
              { key: 'calls', label: 'Calls', defaultWidth: 90, minWidth: 60 },
              { key: 'duration', label: 'Total Duration', defaultWidth: 130, minWidth: 90 },
              { key: 'errorRate', label: 'Error Rate', defaultWidth: 110, minWidth: 80 },
              { key: 'promptTokens', label: 'Prompt Tokens', defaultWidth: 130, minWidth: 90 },
              { key: 'completionTokens', label: 'Completion Tokens', defaultWidth: 160, minWidth: 110 },
              { key: 'totalTokens', label: 'Total Tokens', defaultWidth: 130, minWidth: 90 },
              { key: 'cost', label: 'Total LLM Spend', defaultWidth: 140, minWidth: 100 },
              { key: 'agents', label: 'Agents', defaultWidth: 250, minWidth: 150 },
              { key: 'lastActivity', label: 'Last Activity', defaultWidth: 130, minWidth: 110 },
            ]}
            data={filteredModels}
            renderRow={VirtualModelRow}
            onOpen={openModel}
            storageKey="lumoz-models-table-widths"
            rowHeight={layout.rowHeight}
            containerHeight="calc(100vh - 350px)"
          />
        </div>
      )}
    </div>
  );
}